import { useEffect } from 'react';
import Swal from 'sweetalert2';
import i18n from '@/i18n';

/**
 * Custom hook for handling notifications using SweetAlert2
 * @param {Object} options - Configuration options
 * @param {string} options.error - Error message to display
 * @param {string} options.message - Success message to display
 * @param {function} options.clearError - Function to clear error
 * @param {function} options.clearMessage - Function to clear message
 */
export const useNotifications = ({ error, message, clearError, clearMessage }) => {
  // Handle errors with toast notifications
  useEffect(() => {
    if (error) {
      Swal.fire({
        title: i18n.t('notifications.errorTitle'),
        text: error,
        icon: 'error',
      }).then(() => {
        clearError();
      });
    }
  }, [error, clearError]);

  // Handle success messages
  useEffect(() => {
    if (message) {
      Swal.fire({
        title: i18n.t('notifications.successTitle'),
        text: message,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      }).then(() => {
        clearMessage();
      });
    }
  }, [message, clearMessage]);

  /**
   * Show confirmation dialog for deletion
   * @param {number} itemCount - Number of items to delete
   * @param {string} customText - Optional custom text to display instead of default
   * @returns {Promise<boolean>} - Returns true if confirmed
   */
  const showDeleteConfirmation = async (itemCount, customText) => {
    const defaultText = i18n.t('notifications.deleteDefaultText', { count: itemCount });
    const text = customText || defaultText;
    
    const result = await Swal.fire({
      title: i18n.t('notifications.confirmTitle'),
      text: text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: i18n.t('notifications.confirmDeleteButton'),
      cancelButtonText: i18n.t('notifications.cancelButton')
    });

    return result.isConfirmed;
  };

  /**
   * Show success notification for deletion
   * @param {number} itemCount - Number of items deleted
   * @param {string} customText - Optional custom text to display instead of default
   */
  const showDeleteSuccess = (itemCount, customText) => {
    const defaultText = i18n.t('notifications.deleteSuccessDefault', { count: itemCount });
    const text = customText || defaultText;
    
    Swal.fire({
      title: i18n.t('notifications.deletedTitle'),
      text: text,
      icon: 'success',
      timer: 2000,
      showConfirmButton: false
    });
  };

  return {
    showDeleteConfirmation,
    showDeleteSuccess
  };
};
